import React, { useEffect, useRef, useState } from 'react';
import { ActivityStats } from 'types/interfaces/activity';
import './LivePulseDashboard.css';

interface LivePulseDashboardProps {
  stats: ActivityStats | undefined;
  isLoading: boolean;
  isConnected: boolean;
}

const formatStatNumber = (num: number): string => {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return String(num);
};

/**
 * A single live stat value. Flashes a soft highlight when its value changes so the
 * feed visibly "registers" activity. The first settled value (and any change while
 * loading) does NOT flash, so the initial load is calm.
 */
const StatValue: React.FC<{ value: number; isLoading: boolean }> = ({ value, isLoading }) => {
  const prevRef = useRef(value);
  const settledRef = useRef(false);
  const [bumped, setBumped] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!settledRef.current) {
      settledRef.current = true;
      prevRef.current = value;
      return;
    }
    if (prevRef.current !== value) {
      prevRef.current = value;
      setBumped(true);
    }
  }, [value, isLoading]);

  return (
    <span
      className={`live-pulse-dashboard__stat-value${bumped ? ' live-pulse-dashboard__stat-value--bumped' : ''}`}
      onAnimationEnd={() => setBumped(false)}
    >
      {isLoading ? '—' : formatStatNumber(value)}
    </span>
  );
};

const LivePulseDashboard: React.FC<LivePulseDashboardProps> = ({
  stats,
  isLoading,
  isConnected,
}) => {
  return (
    <div className="live-pulse-dashboard">
      <div className="live-pulse-dashboard__indicator">
        <span
          className={`live-pulse-dashboard__dot${isConnected ? ' live-pulse-dashboard__dot--connected' : ' live-pulse-dashboard__dot--disconnected'}`}
        />
        <span className="live-pulse-dashboard__label">
          {isConnected ? 'Live' : 'Reconnecting'}
        </span>
      </div>

      <div className="live-pulse-dashboard__divider" />

      <div className="live-pulse-dashboard__stats">
        <div className="live-pulse-dashboard__stat">
          <StatValue value={stats?.tradesLastHour ?? 0} isLoading={isLoading} />
          <span className="live-pulse-dashboard__stat-label">trades/hr</span>
        </div>

        <div className="live-pulse-dashboard__divider" />

        <div className="live-pulse-dashboard__stat">
          <StatValue value={stats?.packagesShippedToday ?? 0} isLoading={isLoading} />
          <span className="live-pulse-dashboard__stat-label">shipped today</span>
        </div>

        <div className="live-pulse-dashboard__divider" />

        <div className="live-pulse-dashboard__stat">
          <StatValue value={stats?.cardsTradedThisWeek ?? 0} isLoading={isLoading} />
          <span className="live-pulse-dashboard__stat-label">cards/wk</span>
        </div>
      </div>
    </div>
  );
};

export default LivePulseDashboard;
